import pythonApiClient from './pythonBaseApi';

const permisosApi = {
  /**
   * Verificar si el usuario tiene un permiso específico en un proyecto
   * @param {string} permission - Nombre del permiso (ej: 'depositos.aprobar')
   * @param {number} proyectoId - ID del proyecto
   * @returns {Promise<boolean>} - true si tiene el permiso, false si no
   */
  async checkPermission(permission, proyectoId) {
    try {
      const response = await pythonApiClient.get(`/seguridad/permisos/check/${permission}`, {
        params: { proyecto_id: proyectoId }
      });
      return response.data?.has_permission || false;
    } catch (error) {
      console.error('Error al verificar permiso:', error);
      return false;
    }
  },

  /**
   * Obtener todos los permisos del usuario actual en un proyecto
   * @param {number} userId - ID del usuario
   * @param {number} proyectoId - ID del proyecto (opcional)
   * @returns {Promise<Object>} - Objeto con permisos del usuario
   */
  async getUserPermissions(userId, proyectoId = null) {
    try {
      const params = proyectoId ? { proyecto_id: proyectoId } : {};
      const response = await pythonApiClient.get(`/seguridad/permisos/usuario/${userId}/permisos`, { params });
      return response.data;
    } catch (error) {
      console.error('Error al obtener permisos del usuario:', error);
      return { permisos: [] };
    }
  },

  /**
   * Obtener todos los roles disponibles
   * @returns {Promise<Array>} - Lista de roles
   */
  async getRoles() {
    try {
      const response = await pythonApiClient.get('/seguridad/permisos/roles');
      return response;
    } catch (error) {
      console.error('Error al obtener roles:', error);
      throw error;
    }
  },

  /**
   * Obtener todos los permisos disponibles
   * @returns {Promise<Array>} - Lista de permisos
   */
  async getPermisos() {
    try {
      const response = await pythonApiClient.get('/seguridad/permisos/permisos');
      return response;
    } catch (error) {
      console.error('Error al obtener permisos:', error);
      throw error;
    }
  },

  /**
   * Crear un nuevo rol
   * @param {Object} rolData - Datos del rol {nombre, descripcion}
   * @returns {Promise<Object>} - Rol creado
   */
  async createRole(rolData) {
    try {
      const response = await pythonApiClient.post('/seguridad/permisos/roles', rolData);
      return response;
    } catch (error) {
      console.error('Error al crear rol:', error);
      throw error;
    }
  },

  /**
   * Actualizar un rol
   * @param {number} rolId - ID del rol
   * @param {Object} rolData - Datos del rol {nombre?, descripcion?, activo?}
   * @returns {Promise<Object>} - Rol actualizado
   */
  async updateRole(rolId, rolData) {
    try {
      const response = await pythonApiClient.put(`/seguridad/permisos/roles/${rolId}`, rolData);
      return response;
    } catch (error) {
      console.error('Error al actualizar rol:', error);
      throw error;
    }
  },

  /**
   * Eliminar (desactivar) un rol
   * @param {number} rolId - ID del rol
   * @returns {Promise<Object>} - Respuesta del servidor
   */
  async deleteRole(rolId) {
    try {
      const response = await pythonApiClient.delete(`/seguridad/permisos/roles/${rolId}`);
      return response;
    } catch (error) {
      console.error('Error al eliminar rol:', error);
      throw error;
    }
  },

  /**
   * Obtener permisos de un rol específico
   * @param {number} rolId - ID del rol
   * @returns {Promise<Array>} - Lista de permisos del rol
   */
  async getPermisosRol(rolId) {
    try {
      const response = await pythonApiClient.get(`/seguridad/permisos/roles/${rolId}/permisos`);
      return response;
    } catch (error) {
      console.error('Error al obtener permisos del rol:', error);
      throw error;
    }
  },

  /**
   * Asignar permisos a un rol
   * @param {number} rolId - ID del rol
   * @param {Array<number>} permisoIds - Array de IDs de permisos
   * @returns {Promise<Object>} - Rol actualizado
   */
  async asignarPermisosRol(rolId, permisoIds) {
    try {
      const response = await pythonApiClient.post(`/seguridad/permisos/roles/${rolId}/permisos`, {
        permiso_ids: permisoIds
      });
      return response;
    } catch (error) {
      console.error('Error al asignar permisos al rol:', error);
      throw error;
    }
  }
};

export default permisosApi;
